import React, {useEffect, useState} from "react";
import {useSelector} from "../../../shared-services/redux-tiny";
import BasicFarmyModal from "./BasicFarmyModal";
import useFullLoader from "../../loaders/useFullLoader";
import {ModalTitle, ModalBody, ModalFooter} from "react-bootstrap";
import useModalQueue from "./useModalQueue";
import {t} from "../../../shared-services/translate";
import styled from "styled-components";
import {Link} from "react-router-dom";
import {FaEyeSlash, FaEye, FaArrowLeft} from "react-icons/fa";

const userService = window.UserService;
const alerts = window.Alerts;

const LoginModal = () => {
  const modalName = "LoginModal";
  const {closeModal, pushToQueue} = useModalQueue(modalName);
  const {launchLoader, closeLoader} = useFullLoader();
  const minPasswordLength = 4;

  const {currentModal} = useSelector(state => state.topLayer);

  const [enableEmailValidation, setEmailValidation] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [authInProgress, setAuthInProgress] = useState(false);
  const [authenticationError, setAuthenticationError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [showBackLink, setShowBackLink] = useState(false);
  const [backLinkAction, setBackLinkAction] = useState({});

  const tKey = "sign_in_modal";
  const tShort = (key) => t(`${tKey}.${key}`);

  const onLoginSubmit = (event) => {
    event.preventDefault();

    const elements = event?.currentTarget?.elements;
    const email = elements?.email?.value;
    const password = elements?.password?.value;
    const verifyToken = elements?.verifyToken?.value;

    setAuthInProgress(true);

    launchLoader();
    userService.authenticate(email, password, verifyToken).then(user => {
      if (user.pending_verification) {
        setTwoFactorPending(true);
        setAuthInProgress(false);

        if (user.pending_verification_message) {
          alerts.info(user.pending_verification_message);
        }
      } else {
        setTwoFactorPending(false);
        setAuthInProgress(false);
        closeModal();
      }

      closeLoader();
    }, (error) => {
      setAuthenticationError(true);
      console.error({error});
    }).finally(() => closeLoader());
  };

  const disableValidationMessage = () => {
    setEmailValidation(false);
  };

  const enableValidationMessage = (event) => {
    setEmailValidation(true);
  };

  const validateEmail = (event) => {
    const emailValidity = event?.target?.validity?.valid;
    if (!emailValidity) return;

    if (emailValidity) setEmailValid(true);
    else setEmailValid(false);
  };

  const validatePassword = (event) => {
    const passwordInput = event?.target?.value;

    if (passwordInput && passwordInput.length > minPasswordLength) setPasswordValid(true);
    else setPasswordValid(false);
  };

  const togglePasswordVisible = () => {
    setShowPassword(!showPassword);
  };

  const onBackLinkClick = () => {
    const {modal} = backLinkAction;
    if (modal) pushToQueue(modal?.name);
    closeModal();
  };

  const parentProps = {
    modalName
  };

  useEffect(() => {
    if (currentModal?.name === modalName && currentModal?.showBackLink) {
      setShowBackLink(true);
      setBackLinkAction(currentModal?.backLinkAction || {});
    }
  }, [currentModal]);

  return (
    <BasicFarmyModal {...parentProps} className="loginModal">
      <StyledLoginModal>
        <ModalTitle className="text-align-center margin-bottom-10">
          {tShort("headline")}
        </ModalTitle>

        <ModalBody>
          {showBackLink ? <div className="backButton cursor-pointer" onClick={onBackLinkClick}><FaArrowLeft/></div> : <></> }
          <div className="form-wrapper full-width flex flex-direction-column align-items-center justify-content-center margin-bottom-20">
            <form onSubmit={onLoginSubmit}
              className="flex flex-direction-column align-items-center justify-content-center"
            >
              <div className="form-label-block margin-bottom-10">
                <label className="text-small text-color-dimmed font-family-alata">
                  {tShort("email")}
                </label>
                <div
                  className="input-wrapper flex align-items-center justify-content-space-between input-default overflow-hidden">
                  <input
                    type="email"
                    name="email"
                    className={`input-invisible full-width ${!enableEmailValidation || emailValid ? "" : "text-color-danger"}`}
                    onChange={validateEmail}
                    onFocus={disableValidationMessage}
                    onBlur={enableValidationMessage}
                    autoFocus={true}
                  />
                </div>
              </div>

              <div className="form-label-block margin-bottom-10">
                <label className="text-small text-color-dimmed font-family-alata">
                  {tShort("password")}
                </label>
                <div
                  className="input-wrapper flex align-items-center justify-content-space-between input-default overflow-hidden"
                >
                  <input
                    onChange={validatePassword}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="input-invisible full-width"
                  />
                  <div className="show-password link-styled text-color-dimmed" onClick={togglePasswordVisible}>
                    {showPassword ? <FaEyeSlash/> : <FaEye/>}
                  </div>
                </div>
              </div>

              {twoFactorPending && <div className='form-label-block margin-bottom-10'>
                <label className='text-small text-color-dimmed font-family-alata'>
                  Token from SMS:
                </label>
                <div
                  className='input-wrapper flex align-items-center justify-content-space-between input-default overflow-hidden'>
                  <input
                    name='verifyToken'
                    type='password'
                    className='input-invisible full-width'
                  />
                </div>
              </div>}

              <div className="text-align-right full-width">
                <Link
                  to="/password/recover"
                  className="text-color-dimmed text-decoration-underline font-family-alata"
                  onClick={() => closeModal()}
                >
                  {t("sign_in_modal.forgot")}
                </Link>
              </div>

              {authenticationError &&
                <div className="text-strong text-color-danger margin-top-30">
                  {tShort("login_error")}
                </div>
              }

              <SubmitButton
                disabled={!emailValid || !passwordValid}
                className={emailValid && passwordValid ? "btn-green" : "btn"}
              >
                {tShort("login")}
              </SubmitButton>
            </form>
          </div>
        </ModalBody>

        <ModalFooter className="text-align-center">
          <h3 className="text-strong">{tShort("new_to_farmy")}</h3>
          <Link
            to="/signup"
            className="text-color-body font-family-alata text-small text-decoration-underline"
            onClick={() => closeModal()}
          >
            {tShort("register_here")}
          </Link>
        </ModalFooter>
      </StyledLoginModal>
    </BasicFarmyModal>);
};

export default LoginModal;

const StyledLoginModal = styled.div.attrs()`
  .input-wrapper {
    width: 300px;
  }
  
  .show-password {
    display: flex;
    font-size: 20px;
    margin: 0 7px;
  }
  
  .backButton {
    position: absolute;
    top: -65px;
    left: -10px;
  }
`;

const SubmitButton = styled.button.attrs(props => ({
  className: `${props.className} btn btn-farmy btn-lg login-btn margin-top-30`
}))`
  min-width: 138px;
`;
